import { Pencil, Phone, Trash, WarningCircle, X } from "@phosphor-icons/react";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../api/axiosInstance";
import dayjs from "dayjs";
import PageLoader from "../loader/PageLoader";
import DragDrop from "../DragDrop";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { formatHotel } from "../../api/hotelTime";
import { uploadImageToServer } from "../../api/uploadImage";

const ManageRoom = () => {
    const [loading, setLoading] = useState(false);
    const [currentData, setCurrentData] = useState(null);
    const [virtualRooms, setVirtualRooms] = useState([]);
    const [selectedVirtualRoom, setSelectedVirtualRoom] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [data, setData] = useState([]);
    const [errors, setErrors] = useState({});
    const hotelId = localStorage.getItem("hotelId");
    const [userVerified, setUserVerified] = useState(false);
    const [userExist, setUserExist] = useState(true);
    const [showUserForm, setShowUserForm] = useState(false);
    const [guests, setGuests] = useState([]);
    const [editingGuest, setEditingGuest] = useState(null);
    const [roomNumber, setRoomNumber] = useState("");
    const [initialState, setInitialState] = useState(null);
    const [bookingData, setBookingData] = useState([]);
    const [isCheckoutCompleted, setIsCheckoutCompleted] = useState(false);
    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        countryCode: "91", // Default country code
        verification_id: "",
        payment_status: "",
    });
    const { id, roomId } = useParams();
    const navigate = useNavigate();
    // const { selectedBookedRoom } = useOutletContext();
    const location = useLocation();
    const initialRoomId = roomId;

    useEffect(() => {
        if (!id || !initialRoomId) {
            navigate(-1);
            return;
        }
    }, [id, initialRoomId, navigate]);

    const splitPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return { countryCode: "91", contact: "" };

        const numberStr = phoneNumber.toString();

        if (numberStr.length <= 10) {
            return { countryCode: "91", contact: numberStr };
        }

        if (numberStr.length === 11) {
            return {
                countryCode: numberStr.substring(0, 1),
                contact: numberStr.substring(1),
            };
        }

        if (numberStr.length === 12) {
            return {
                countryCode: numberStr.substring(0, 2),
                contact: numberStr.substring(2),
            };
        }

        // For numbers longer than 12 digits, assume first 2-3 digits are country code
        return {
            countryCode: numberStr.substring(0, numberStr.length - 10),
            contact: numberStr.substring(numberStr.length - 10),
        };
    };

    useEffect(() => {
        checkForChanges();
    }, [guests, roomNumber, formData.checkin_status, formData.payment_status]);

    const checkForChanges = () => {
        if (!initialState) return false;

        const hasGuestsChanged =
            JSON.stringify(guests) !== JSON.stringify(initialState.guests);
        const hasRoomNumberChanged = roomNumber !== initialState.roomNumber;
        const hasCheckinStatusChanged =
            formData.checkin_status !== initialState.checkin_status;
        const hasPaymentStatusChanged =
            formData.payment_status !== initialState.payment_status;

        const changesExist =
            hasGuestsChanged ||
            hasRoomNumberChanged ||
            hasCheckinStatusChanged ||
            hasPaymentStatusChanged;

        setHasUnsavedChanges(changesExist);
        return changesExist;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const virtualRoomsResponse = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${id}/booked-rooms`,
                );

                const virtualRooms = virtualRoomsResponse.data?.data || [];
                setVirtualRooms(virtualRooms);

                if (virtualRooms.length > 0) {
                    const initialRoom = virtualRooms.find(
                        (room) => room.id === initialRoomId,
                    );

                    setSelectedVirtualRoom(initialRoom);
                } else {
                    setSelectedVirtualRoom(null);
                }

                const roomResponse = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/bookings/${id}/booked-rooms/${initialRoomId}`,
                );
                setIsCheckoutCompleted(
                    roomResponse.data.is_checkout_completed || false,
                );
                setCurrentData(roomResponse.data);
                setRoomNumber(roomResponse.data.room_number?.toString() || "");

                const bookingResponse = await axiosInstance.get(
                    `api/v1/hotels/${hotelId}/bookings/${id}`,
                );

                setBookingData({
                    ...bookingResponse.data,
                    checkin_date: formatHotel(bookingResponse.data.checkin_date, "YYYY-MM-DD"),
                    checkout_date: formatHotel(bookingResponse.data.checkout_date, "YYYY-MM-DD"),
                });

                setFormData((prev) => ({
                    ...prev,
                    checkin_status: roomResponse.data.is_checkin_completed
                        ? "Completed"
                        : "Pending",
                    payment_status:
                        roomResponse.data.payment_status === "SUCCESSFUL"
                            ? "SUCCESSFUL"
                            : "",
                }));
                const guests = roomResponse.data.guests || [];
                const guestData = await Promise.all(
                    guests.map(async (guest) => {
                        const imageResponse = await getImageByVerificationId(
                            guest.verification_id,
                        );
                        const { countryCode, contact } = splitPhoneNumber(
                            guest.contact,
                        );
                        return {
                            ...guest,
                            image: imageResponse,
                            countryCode,
                            displayContact: contact,
                        };
                    }),
                );
                setGuests(guestData);
                setInitialState({
                    guests: guestData,
                    roomNumber: roomResponse.data.room_number?.toString() || "",
                    checkin_status: roomResponse.data.is_checkin_completed
                        ? "Completed"
                        : "Pending",
                    payment_status:
                        roomResponse.data.payment_status === "SUCCESSFUL"
                            ? "SUCCESSFUL"
                            : "",
                });

                setLoading(false);
            } catch (err) {
                setErrors({ fetch: "Failed to fetch Extend Room data" });
                setLoading(false);
            }
        };

        fetchData();
    }, [hotelId, initialRoomId, id]);

    const handleVerifyUser = async () => {
        try {
            if (!formData.contact) {
                setErrors({
                    ...errors,
                    verifyContact: "Contact is required field",
                });
                return;
            }

            const fullContact = parseInt(
                formData.countryCode + formData.contact,
            );
            const response = await axiosInstance.get(
                `/api/v1/users?contact=${fullContact}`,
            );
            if (response.data.data.length > 0) {
                const userData = response.data.data[0];
                const { countryCode, contact } = splitPhoneNumber(
                    userData.contact,
                );
                setFormData({
                    ...userData,
                    countryCode,
                    contact,
                });
                setUserVerified(true);
            } else {
                setFormData({
                    name: "",
                    countryCode: formData.countryCode,
                    contact: formData.contact,
                    verification_id: "",
                });
                setUserVerified(false);
            }
            setShowUserForm(true);
            setUserExist(false);
        } catch (error) {
            console.error("User verification failed:", error);
        }
    };

    const handleChange = (e) => {
        setErrors("");
        const { value, name } = e.target;
        if (name === "contact") {
            // Only allow numbers and limit to 10 digits
            const numbers = value.replace(/\D/g, "");
            if (numbers.length <= 10) {
                setFormData((prev) => ({ ...prev, [name]: numbers }));
            }
            return;
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name is required!";
        if (!formData.contact) newErrors.contact = "Contact is required";
        if (!formData.verification_id)
            newErrors.verification_id = "Verification Id is required!";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleAddGuest = async () => {
        if (isCheckoutCompleted) {
            alert("Cannot add guests after checkout is completed.");
            return;
        }
        if (!validateForm()) return;

        const imageUrl = await getImageByVerificationId(
            formData.verification_id,
        );
        const fullContact = parseInt(formData.countryCode + formData.contact);
        const newGuest = {
            ...formData,
            image: imageUrl,
            contact: fullContact,
            countryCode: formData.countryCode,
            displayContact: formData.contact,
        };

        setGuests((prev) => [...prev, newGuest]);
        setFormData({
            name: "",
            contact: "",
            countryCode: "91",
            verification_id: "",
        });
        setUserExist(true);
        setUserVerified(false);
    };

    const getImageByVerificationId = async (key) => {
        try {
            const response = await axiosInstance.get(
                `/api/v1/images/download-presigned-url?key=${key}`,
            );
            const imageUrl = response.data.url;
            return imageUrl;
        } catch (error) {
            console.error("Error fetching image URL:", error);
            return { key, url: "", loading: false, error: true };
        }
    };

    const handleEditGuest = (guest) => {
        if (isCheckoutCompleted) {
            alert("Cannot edit guests after checkout is completed.");
            return;
        }
        const { countryCode, contact } = splitPhoneNumber(guest.contact);
        setUserExist(false);
        setFormData({
            ...guest,
            countryCode,
            contact,
        });
        setEditingGuest(guest);
    };
    const handleDeleteGuest = (guestToDelete) => {
        if (isCheckoutCompleted) {
            alert("Cannot edit guests after checkout is completed.");
            return;
        }
        setGuests((prev) =>
            prev.filter((guest) => guest.contact !== guestToDelete.contact),
        );
    };

    // A room number is the check-in: reassigning it needs the open requests, the
    // PMS and the guest's welcome details moved with it, none of which this screen
    // does. Assign once here; moves go through a Room Change request.
    const isRoomNumberLocked = Boolean(initialState?.roomNumber);

    const handleUpdateRoom = async () => {
        try {
            const updateData = {
                room_number: parseInt(roomNumber),
                guest_details: guests.map((guest) => ({
                    name: guest.name,
                    contact: parseInt(guest.countryCode + guest.displayContact),
                    verification_id: guest.verification_id,
                })),
                number_of_guests: guests.length,
                is_checkin_completed: formData.checkin_status === "Completed",
                payment_status: formData.payment_status,
            };

            // console.log(">>>>>>>>>>", selectedVirtualRoom);

            await axiosInstance.put(
                `/api/v1/hotels/${hotelId}/bookings/${selectedVirtualRoom.booking_id}/booked-rooms/${selectedVirtualRoom.id}`,
                updateData,
            );

            setInitialState({
                guests,
                roomNumber,
                checkin_status: formData.checkin_status,
                payment_status: formData.payment_status,
            });
            setHasUnsavedChanges(false);

            // handleClose(); // Close modal after successful update
        } catch (error) {
            console.error("Error updating room:", error);
            setErrors({ update: "Failed to update room" });
        }
    };

    //working
    const handleFileUpload = async (files) => {
        if (files.length === 0) return;

        try {
            const file = files[0];
            const uploadUrlResponse = await axiosInstance.get(
                `/api/v1/images/upload-presigned-url?file_name=${file.name}`,
            );
            const { key, url } = uploadUrlResponse.data;
            const isSuccess = await uploadImageToServer(file, url);

            if (isSuccess) {
                setFormData((prev) => ({
                    ...prev,
                    verification_id: key,
                }));
                return;
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            return false;
        }
    };

    const handleUpdateGuest = () => {
        const fullContact = parseInt(formData.countryCode + formData.contact);
        const updatedGuest = {
            ...formData,
            contact: fullContact,
            displayContact: formData.contact,
            countryCode: formData.countryCode,
        };

        const updatedGuests = guests.map((g) =>
            g === editingGuest ? updatedGuest : g,
        );
        setGuests(updatedGuests);
        setEditingGuest(null);
        setFormData({
            name: "",
            contact: "",
            countryCode: "91",
            verification_id: "",
        });
    };

    const formatContact = (contact) => {
        if (!contact) return ""; // Return empty string if no contact is available

        // Convert contact to a string and remove any non-digit characters
        const cleanNumber = contact.toString().replace(/\D/g, "");

        // If contact is more than 10 digits, assume the first part is the country code
        if (cleanNumber.length > 10) {
            const countryCode = cleanNumber.slice(0, cleanNumber.length - 10);
            const actualContact = cleanNumber.slice(-10);
            return `+${countryCode} - ${actualContact}`;
        }

        // If contact is 10 digits or less, return it as is
        return cleanNumber;
    };

    const handleClose = () => {
        // Get the current path
        const currentPath = window.location.pathname;

        // Find the index of "manage-room" or "manage-guests"
        const splitPath = currentPath.split("/");
        // console.log("triggering the close....", splitPath);
        const manageIndex = splitPath.findIndex(
            (segment) =>
                segment === "manage-room" || segment === "manage-guest",
        );

        if (manageIndex !== -1) {
            // Reconstruct the path up to "manage-room" or "manage-guests"
            const updatedPath = splitPath.slice(0, manageIndex).join("/");
            // Navigate to the updated path
            navigate(updatedPath);
        } else {
            console.warn(
                "No 'manage-room' or 'manage-guests' found in the URL",
            );
        }
    };

    const handleVirtualRoomSelect = async (room) => {
        setSelectedVirtualRoom(room);
        // console.log(")))))))))))))))", room);
        if (hasUnsavedChanges) {
            if (
                !window.confirm(
                    "You have unsaved changes. Do you want to discard them?",
                )
            ) {
                return;
            }
        }

        try {
            setLoading(true);
            const response = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/bookings/${room.booking_id}/booked-rooms/${room.id}`,
            );
            // console.log(
            //     "&&&&&&&&&&&&&&&&&&& checkout value",
            //     response.data.is_checkout_completed,
            // );
            setIsCheckoutCompleted(
                response.data.is_checkout_completed || false,
            );

            setCurrentData(response.data);
            setRoomNumber(response.data.room_number?.toString() || "");

            const guestData = await Promise.all(
                (response.data.guests || []).map(async (guest) => {
                    const imageResponse = await getImageByVerificationId(
                        guest.verification_id,
                    );
                    const { countryCode, contact } = splitPhoneNumber(
                        guest.contact,
                    );
                    return {
                        ...guest,
                        image: imageResponse,
                        countryCode,
                        displayContact: contact,
                    };
                }),
            );
            setGuests(guestData);
            setFormData({
                ...formData,
                checkin_status: response.data.is_checkin_completed
                    ? "Completed"
                    : "Pending",
                payment_status:
                    response.data.payment_status === "SUCCESSFUL"
                        ? "SUCCESSFUL"
                        : "",
            });

            // Update initial state for the new room
            setInitialState({
                guests: guestData,
                roomNumber: response.data.room_number?.toString() || "",
                checkin_status: response.data.is_checkin_completed
                    ? "Completed"
                    : "Pending",
                payment_status:
                    response.data.payment_status === "SUCCESSFUL"
                        ? "SUCCESSFUL"
                        : "",
            });

            setHasUnsavedChanges(false);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching room data:", error);
            setLoading(false);
        }
    };

    let nullRoomIndex = 1;
    const processedData =
        virtualRooms &&
        virtualRooms
            .sort((a, b) => a.id.localeCompare(b.id))
            .map((room) => ({
                ...room,
                room_number: String(nullRoomIndex++),
            }));

    return (
        <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-60 flex items-center justify-center z-[1001] ">
            {loading && <PageLoader />}
            <form
                className="popup-content bg-white p-6 rounded-lg text-sm flex flex-col gap-2 w-[80%] md:w-[70%] lg:w-[50%] overflow-y-auto"
                onSubmit={(e) => e.preventDefault()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold">Manage Room</h2>
                    <button
                        onClick={handleClose}
                        className="hover:bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                        type="button"
                    >
                        <X weight="bold" size={18} />
                    </button>
                </div>
                <div className="flex items-center justify-between">
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">Booking ID</p>
                        <p className="truncate pr-3" title={id}>
                            {id}
                        </p>
                    </div>
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">OTA ID</p>
                        <input
                            type="text"
                            className="p-1 rounded-md border-[1.5px] w-full"
                            name="ota_id"
                            value={bookingData?.ota_id}
                        />
                    </div>
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">Check-in Date</p>
                        <input
                            type="date"
                            className="p-1 rounded-md border-[1.5px]"
                            name="checkin_date"
                            value={bookingData?.checkin_date}
                            // onChange={handleChange}
                        />
                    </div>
                    <div className="w-1/4">
                        <p className="text-[#8C9199] mb-2">Check-out Date</p>
                        <input
                            type="date"
                            className="p-1 rounded-md border-[1.5px]"
                            name="checkout_date"
                            value={bookingData?.checkout_date}
                            // onChange={handleChange}
                        />
                    </div>
                </div>
                <div className="flex gap-3 mt-2">
                    <div className="w-1/2">
                        <p className="text-[#262B33] font-medium mb-1">
                            Check-in Status
                        </p>
                        <select
                            name="checkin_status"
                            value={formData.checkin_status}
                            onChange={handleChange}
                            className="w-full border-[1.5px] p-1.5 rounded-md"
                        >
                            <option value="Completed">Completed</option>
                            <option value="Pending">Pending</option>
                        </select>
                    </div>
                    <div className="w-1/2">
                        <p className="text-[#262B33] font-medium mb-1">
                            Payment Status
                        </p>
                        <select
                            name="payment_status"
                            value={
                                formData.payment_status === "SUCCESSFUL"
                                    ? "Completed"
                                    : "Pending"
                            }
                            onChange={(e) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    payment_status:
                                        e.target.value === "Completed"
                                            ? "SUCCESSFUL"
                                            : "",
                                }))
                            }
                            className="w-full border-[1.5px] p-1.5 rounded-md"
                        >
                            <option value="">Pending</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>
                </div>

                <div className="flex gap-2 overflow-x-auto max-w-full py-2">
                    {processedData.map((room) => (
                        <button
                            key={room.id}
                            onClick={() => handleVirtualRoomSelect(room)}
                            className={`px-4 py-2 rounded-lg flex-shrink-0 flex items-center gap-2 ${
                                selectedVirtualRoom?.id === room.id
                                    ? "bg-[#ff7378] text-white"
                                    : "bg-gray-100"
                            }`}
                        >
                            <span>Room {room.room_number ?? "NA"}</span>
                            {room.requests && room.requests.length > 0 && (
                                <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded-full text-xs">
                                    {room.requests.length}
                                </span>
                            )}
                            {/* <span className="text-xs text-gray-500">
                                {room.is_checkin_completed
                                    ? "Checked In"
                                    : room.is_checkout_completed
                                    ? "Checked Out"
                                    : "Pending Checkin"}
                            </span> */}
                        </button>
                    ))}
                </div>

                <div className="flex border-t-[1.5px]">
                    <div className="w-[40%] p-2 border-r-[1.5px]">
                        {!isCheckoutCompleted &&
                            (editingGuest ? (
                                <button
                                    onClick={handleUpdateGuest}
                                    className="bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full mb-2"
                                >
                                    Update Guest
                                </button>
                            ) : (
                                <button
                                    onClick={handleAddGuest}
                                    className="bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full mb-2"
                                >
                                    Add Guest
                                </button>
                            ))}

                        <div className="h-56 overflow-auto">
                            {guests &&
                                guests.map((guest, index) => (
                                    <div
                                        className="border-[1.5px] rounded-lg overflow-hidden h-full bg-[#EEF0FB]"
                                        key={index}
                                    >
                                        <img
                                            src={guest.image}
                                            alt="verification_id"
                                            className="h-36 w-full bg-center"
                                        />
                                        <div className="px-1.5 text-xs">
                                            <p className="text-[#262B33] font-bold text-lg">
                                                {guest?.name}
                                            </p>
                                            <p>
                                                {formatContact(guest?.contact)}
                                            </p>
                                            {!isCheckoutCompleted && (
                                                <div className="flex items-center justify-end">
                                                    <div className="flex items-center gap-1">
                                                        <button
                                                            className="border-[#F5CCD3] text-[#D02746] p-1 rounded-md border-[1.5px] bg-white"
                                                            onClick={() =>
                                                                handleDeleteGuest(
                                                                    guest,
                                                                )
                                                            }
                                                        >
                                                            <Trash size={16} />
                                                        </button>
                                                        <button
                                                            className="border-gray-400 p-1 rounded-md border-[1.5px] bg-white"
                                                            onClick={() =>
                                                                handleEditGuest(
                                                                    guest,
                                                                )
                                                            }
                                                        >
                                                            <Pencil size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                        </div>
                    </div>
                    <div className="w-[60%] p-2 flex flex-col gap-2">
                        {userExist ? (
                            <div>
                                <p className="text-[#262B33] font-medium mb-1">
                                    Phone
                                </p>
                                <div className="border-[1.5px] rounded-md flex w-full overflow-hidden">
                                    <select
                                        name="countryCode"
                                        value={formData.countryCode}
                                        onChange={handleChange}
                                        className="p-1.5 w-[50%] border-r-[1.5px] outline-none"
                                    >
                                        <option value="91">India (+91)</option>
                                        <option value="1">USA (+1)</option>
                                        <option value="44">UK (+44)</option>
                                        <option value="86">China (+86)</option>
                                        <option value="81">Japan (+81)</option>
                                        {/* Add more country codes as needed */}
                                    </select>
                                    <div className="rounded-md w-full flex items-center gap-2 px-1.5">
                                        <Phone size={19} />
                                        <input
                                            type="text"
                                            placeholder="Enter your name"
                                            className="w-full py-1.5 outline-none"
                                            name="contact"
                                            value={formData.contact}
                                            onChange={handleChange}
                                            maxLength={10}
                                        />
                                    </div>
                                </div>
                                {errors.verifyContact && (
                                    <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                                        <WarningCircle size={14} />
                                        {errors.verifyContact}
                                    </p>
                                )}
                                <button
                                    className="bg-[#ff7378] p-1.5 rounded-md outline-none border-none w-full my-2"
                                    onClick={handleVerifyUser}
                                >
                                    Verify User
                                </button>
                            </div>
                        ) : (
                            <>
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Name
                                    </p>
                                    <input
                                        type="text"
                                        placeholder="Enter your name"
                                        className="p-1.5 rounded-md border-[1.5px] w-full"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && (
                                        <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                                            <WarningCircle size={14} />
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Phone
                                    </p>
                                    <div className="border-[1.5px] rounded-md flex w-full overflow-hidden">
                                        <select
                                            name="countryCode"
                                            value={formData.countryCode}
                                            onChange={handleChange}
                                            className="p-1.5 w-[50%] border-r-[1.5px] outline-none"
                                        >
                                            <option value="91">
                                                India (+91)
                                            </option>
                                            <option value="1">USA (+1)</option>
                                            <option value="44">UK (+44)</option>
                                            <option value="86">
                                                China (+86)
                                            </option>
                                            <option value="81">
                                                Japan (+81)
                                            </option>
                                            {/* Add more country codes as needed */}
                                        </select>
                                        <div className="rounded-md w-full flex items-center gap-2 px-1.5">
                                            <Phone size={19} />
                                            <input
                                                type="text"
                                                placeholder="Enter your name"
                                                className="w-full py-1.5 outline-none"
                                                name="contact"
                                                value={formData.contact}
                                                onChange={handleChange}
                                                maxLength={10}
                                            />
                                        </div>
                                    </div>
                                    {errors.contact && (
                                        <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                                            <WarningCircle size={14} />
                                            {errors.contact}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <p>Room Number</p>
                                    <input
                                        type="number"
                                        className={`w-full p-1.5 outline-none border-[1.5px] rounded-md ${
                                            isRoomNumberLocked
                                                ? "bg-[#F5F6FA] text-[#98A0AE] cursor-not-allowed"
                                                : ""
                                        }`}
                                        name="room_number"
                                        placeholder="Enter room number"
                                        onChange={(e) =>
                                            setRoomNumber(e.target.value)
                                        }
                                        value={roomNumber}
                                        disabled={isRoomNumberLocked}
                                    />
                                </div>
                                <div>
                                    <p className="text-[#262B33] font-medium mb-1">
                                        Verification ID
                                    </p>
                                    {errors.verification_id && (
                                        <p className="text-[#ff7378] flex gap-1 items-center text-xs">
                                            <WarningCircle size={14} />
                                            {errors.verification_id}
                                        </p>
                                    )}
                                    {formData.verification_id ? (
                                        <div className="flex justify-between items-center p-2 border rounded text-green-300">
                                            <span>
                                                {formData.verification_id}
                                            </span>
                                            <button
                                                onClick={() =>
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        verification_id: "",
                                                    }))
                                                }
                                                className="text-red-500"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    ) : (
                                        <DragDrop
                                            onFilesUploaded={handleFileUpload}
                                            acceptedFileTypes={{
                                                "image/*": [
                                                    ".jpeg",
                                                    ".png",
                                                    ".jpg",
                                                ],
                                            }}
                                            maxFiles={1}
                                            dragDropText="Drag and drop ID image here"
                                        />
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
                <button
                    className="p-2 w-full  rounded-md  bg-[#ff7378] font-medium text-[#262B33]"
                    onClick={handleUpdateRoom} // Respond to guest
                    type="button"
                >
                    Update Room
                </button>
            </form>
        </div>
    );
};

export default ManageRoom;
